import { ace, loan_application } from '@axo/shared/data-access/types';
import {
  Button,
  NonIdealState,
  NonIdealStateIconSize,
  Popover,
  Tab,
  Tabs,
} from '@blueprintjs/core';
import { useContext, useId, useState } from 'react';
import styles from './Notifications.module.css';
import classes from '../CommunicationCard/CommunicationCard.module.css';
import { Notification } from './Notification.tsx';
import { FollowupList } from './FollowupList.tsx';
import { useNavigate } from 'react-router-dom';
import { CommunicationType } from '../../models/communication.ts';
import { ApplicationContext } from '../../store/context.ts';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { useRecentUnreadNotifications } from '@axo/shared/data-access/hooks/services/ace/useRecentUnreadNotifications';
import { useRecentReadNotifications } from '@axo/shared/data-access/hooks/services/ace/useRecentReadNotifications';
import { useReadNotification } from '@axo/shared/data-access/hooks/services/ace/useReadNotification';
import { useUnreadNotification } from '@axo/shared/data-access/hooks/services/ace/useUnreadNotification';
import { useComments } from '@axo/shared/data-access/hooks/queries/useComments';

const UPCOMING_FOLLOWUP_WINDOW_HOURS = 12;
const UPCOMING_FOLLOWUP_WINDOW_MINUTES = UPCOMING_FOLLOWUP_WINDOW_HOURS * 60;

export function Notifications({ marketCountry }: { marketCountry: string }) {
  const TABS_ID = useId();

  const [open, setOpen] = useState(false);

  const unread = useRecentUnreadNotifications(marketCountry);
  const read = useRecentReadNotifications(marketCountry);

  const {
    state: {
      user: { clientID },
    },
  } = useContext(DataAccessContext);
  const followups = useComments({
    OwnerID: clientID,
    FollowupAtInMinutes: UPCOMING_FOLLOWUP_WINDOW_MINUTES,
  });
  const followupWindowLabel = `next ${UPCOMING_FOLLOWUP_WINDOW_HOURS} hours`;
  const followupItems = followups.data?.Items ?? [];

  function close() {
    setOpen(false);
  }

  return (
    <Popover
      isOpen={open}
      onClose={close}
      interactionKind="click"
      placement="auto"
      popoverClassName={styles.popover}
      modifiers={{ arrow: { enabled: false } }}
      content={
        <div>
          <Tabs id={TABS_ID} className={styles.tabs}>
            <Tab
              className={classes.tab}
              id="unread"
              title={`Active (${unread.length})`}
              panel={
                <NotificationList
                  notifications={unread}
                  emptyText="New notifications will appear here."
                  close={close}
                />
              }
            />
            <Tab
              className={classes.tab}
              id="followups"
              title={`Upcoming (${followupItems.length})`}
              panel={
                <FollowupList
                  followups={followupItems}
                  marketCountry={marketCountry}
                  windowLabel={followupWindowLabel}
                  close={close}
                />
              }
            />
            <Tab
              className={classes.tab}
              id="read"
              title={`Last 30 days (${read.length})`}
              panel={
                <NotificationList
                  notifications={read}
                  emptyText="Handled notifications will appear here."
                  close={close}
                />
              }
            />
          </Tabs>
        </div>
      }
      renderTarget={({ isOpen: _, ...props }) => (
        <Button
          {...props}
          small
          onClick={() => setOpen(!open)}
          text={
            followupItems.length > 0
              ? `${unread.length} (${followupItems.length})`
              : `${unread.length}`
          }
          rightIcon="caret-down"
          intent={
            unread.length > 0
              ? 'danger'
              : followupItems.length > 0
                ? 'warning'
                : 'none'
          }
          icon="notifications"
        />
      )}
    />
  );
}

function NotificationList({
  notifications,
  emptyText,
  close,
}: {
  notifications: ace.Notification[];
  emptyText: string;
  close: () => void;
}) {
  const navigate = useNavigate();
  const { dispatch } = useContext(ApplicationContext);
  const read = useReadNotification();
  const unread = useUnreadNotification();

  async function handleClick(
    notification: ace.Notification,
    loanApplication?: loan_application.LoanApplication,
  ) {
    if (!notification.ReadAt) {
      read.mutateAsync({
        id: notification.ID,
        marketCountry: notification.MarketCountry,
      });
    }

    if (notification.LoanApplicationID) {
      dispatch({
        type: 'Open application',
        scope: {
          parentType: 'navigation',
          id: notification.LoanApplicationID,
        },
        payload: {
          humanReadableID: loanApplication?.HumanReadableID ?? '',
          marketCountry: notification.MarketCountry,
        },
      });
      navigate(`/application/${notification.LoanApplicationID}`);
      dispatch({
        type: 'Set communication tab',
        scope: {
          parentType: 'openApplications',
          id: notification.LoanApplicationID,
        },
        payload: CommunicationType.SMS,
      });
      close();
    }
  }

  if (notifications.length < 1) {
    return (
      <div className={styles.noContent}>
        <NonIdealState
          iconSize={NonIdealStateIconSize.SMALL}
          icon="notifications-updated"
          title="No notifications to show"
          description={emptyText}
        />
      </div>
    );
  }

  return (
    <div className={styles.content}>
      {notifications.map((notification) => (
        <Notification
          key={notification.ID}
          notification={notification}
          onClick={(loanApplication?: loan_application.LoanApplication) =>
            handleClick(notification, loanApplication)
          }
          action={
            notification.ReadAt
              ? {
                  label: 'Mark as unread',
                  onClick: () =>
                    unread.mutateAsync({
                      id: notification.ID,
                      marketCountry: notification.MarketCountry,
                    }),
                  isLoading:
                    unread.isLoading &&
                    unread.variables?.id === notification.ID,
                }
              : undefined
          }
        />
      ))}
    </div>
  );
}
